import { CustomReport } from '@principle-theorem/principle-core';
import {
  DateFilterType,
  ICustomReportFilter,
  MeasureReducer,
  PatientStatus,
  ReportBuilderDataSourceId,
  ReportFilterComparison,
  TimingDirection,
  TimingUnit,
} from '@principle-theorem/principle-core/interfaces';
import { ALL_REGIONS, Region } from '@principle-theorem/shared';
import {
  CustomReportTemplateTag,
  ICustomReportTemplate,
} from './custom-report-templates';
import { ChartFilterDefinition } from './data-sources/charts/chart-filter-definition';
import { ReportBuilderCharts } from './data-sources/charts/report-builder-charts';
import { CustomReportFilter } from './models/custom-report-filters';
import { PatientEventFactMeasures } from './models/facts/patient-event-fact';
import { toMeasureBuilderData } from './models/measure-properties';
import { ReportBuilderColumns } from './report-builder-columns';

function basicPatientsCampaignReportTemplate(
  factTable: PatientEventFactMeasures,
  name: string,
  description: string,
  filters: ICustomReportFilter[],
  regions: Region[]
): ICustomReportTemplate {
  return {
    name: name,
    description: description,
    regions,
    tags: [
      CustomReportTemplateTag.Patients,
      CustomReportTemplateTag.Template,
      CustomReportTemplateTag.MarketingCampaign,
    ],
    dataSourceId: ReportBuilderDataSourceId.Patients,
    staticFilters: filters,
    chartSections: [
      CustomReport.chartSection('Charts', [
        ReportBuilderCharts.toCustomReportChart(
          factTable.patient.name,
          ChartFilterDefinition.numberSummary(
            'Patients',
            factTable.patient.name,
            [
              toMeasureBuilderData(
                factTable.patient.count,
                MeasureReducer.Sum,
                'Patients'
              ),
            ]
          )
        ),
      ]),
    ],
    columns: ReportBuilderColumns.toColumns([
      factTable.patient.name,
      factTable.patient.email,
      factTable.patient.mobileNumber,
      factTable.patient.ref,
      factTable.patient.hasOptedInToMarketingCampaigns,
      factTable.patientLink,
    ]),
  };
}

export function activePatientsCampaignReportTemplate(
  factTable: PatientEventFactMeasures
): ICustomReportTemplate {
  return basicPatientsCampaignReportTemplate(
    factTable,
    'Active Patients Report',
    'Report template for targeting active patients whose last visit was within the past 24 months.',
    [
      CustomReportFilter.init({
        id: factTable.patient.status.metadata.id,
        comparison: ReportFilterComparison.IsEqualTo,
        values: [{ label: 'Active', value: PatientStatus.Active }],
      }),
      CustomReportFilter.init({
        id: factTable.patient.lastVisitDate.metadata.id,
        comparison: ReportFilterComparison.DateIsAtMost,
        dateType: DateFilterType.ReportDate,
        relativeOffset: {
          amount: 2,
          unit: TimingUnit.Years,
          direction: TimingDirection.Before,
        },
        values: [],
      }),
    ],
    [Region.Australia]
  );
}

export function patientBirthdayCampaignReportTemplate(
  factTable: PatientEventFactMeasures
): ICustomReportTemplate {
  return basicPatientsCampaignReportTemplate(
    factTable,
    'Birthday Campaign Report',
    'Report template for targeting active patients with upcoming birthdays whose last visit was within the past 24 months.',
    [
      CustomReportFilter.init({
        id: factTable.patient.status.metadata.id,
        comparison: ReportFilterComparison.IsEqualTo,
        values: [{ label: 'Active', value: PatientStatus.Active }],
      }),
      CustomReportFilter.init({
        id: factTable.patient.birthday.metadata.id,
        comparison: ReportFilterComparison.DateIs,
        dateType: DateFilterType.ReportDate,
        values: [],
      }),
      CustomReportFilter.init({
        id: factTable.patient.lastVisitDate.metadata.id,
        comparison: ReportFilterComparison.DateIsAtMost,
        dateType: DateFilterType.ReportDate,
        relativeOffset: {
          amount: 2,
          unit: TimingUnit.Years,
          direction: TimingDirection.Before,
        },
        values: [],
      }),
    ],
    ALL_REGIONS
  );
}

export function reactivationNonHealthFundPatientsCampaignReportTemplate(
  factTable: PatientEventFactMeasures
): ICustomReportTemplate {
  return basicPatientsCampaignReportTemplate(
    factTable,
    'Reactivation Non Health Fund Patients (12-24 Months Inactive)',
    'Report template for targeting active non health fund patients who have been inactive for 12–24 months and have no next visit booked.',
    [
      CustomReportFilter.init({
        id: factTable.patient.status.metadata.id,
        comparison: ReportFilterComparison.IsEqualTo,
        values: [{ label: 'Active', value: PatientStatus.Active }],
      }),
      CustomReportFilter.init({
        id: factTable.patient.lastVisitDate.metadata.id,
        comparison: ReportFilterComparison.DateIsAtLeast,
        dateType: DateFilterType.ReportDate,
        relativeOffset: {
          amount: 12,
          unit: TimingUnit.Months,
          direction: TimingDirection.Before,
        },
        values: [],
      }),
      CustomReportFilter.init({
        id: factTable.patient.lastVisitDate.metadata.id,
        comparison: ReportFilterComparison.DateIsAtMost,
        dateType: DateFilterType.ReportDate,
        relativeOffset: {
          amount: 24,
          unit: TimingUnit.Months,
          direction: TimingDirection.Before,
        },
        values: [],
      }),
      CustomReportFilter.init({
        id: factTable.patient.nextVisitBooked.metadata.id,
        comparison: ReportFilterComparison.IsEqualTo,
        values: [{ label: 'No', value: false }],
      }),
      CustomReportFilter.init({
        id: factTable.patient.hasHealthFundCard.metadata.id,
        comparison: ReportFilterComparison.IsEqualTo,
        values: [{ label: 'No', value: false }],
      }),
    ],
    [Region.Australia]
  );
}

export function reactivationHealthFundPatientsCampaignReportTemplate(
  factTable: PatientEventFactMeasures
): ICustomReportTemplate {
  return basicPatientsCampaignReportTemplate(
    factTable,
    'Reactivation Health Fund Patients (12-24 Months Inactive)',
    'Report template for targeting active health fund patients who have been inactive for 12–24 months and have no next visit booked.',
    [
      CustomReportFilter.init({
        id: factTable.patient.status.metadata.id,
        comparison: ReportFilterComparison.IsEqualTo,
        values: [{ label: 'Active', value: PatientStatus.Active }],
      }),
      CustomReportFilter.init({
        id: factTable.patient.lastVisitDate.metadata.id,
        comparison: ReportFilterComparison.DateIsAtLeast,
        dateType: DateFilterType.ReportDate,
        relativeOffset: {
          amount: 12,
          unit: TimingUnit.Months,
          direction: TimingDirection.Before,
        },
        values: [],
      }),
      CustomReportFilter.init({
        id: factTable.patient.lastVisitDate.metadata.id,
        comparison: ReportFilterComparison.DateIsAtMost,
        dateType: DateFilterType.ReportDate,
        relativeOffset: {
          amount: 24,
          unit: TimingUnit.Months,
          direction: TimingDirection.Before,
        },
        values: [],
      }),
      CustomReportFilter.init({
        id: factTable.patient.nextVisitBooked.metadata.id,
        comparison: ReportFilterComparison.IsEqualTo,
        values: [{ label: 'No', value: false }],
      }),
      CustomReportFilter.init({
        id: factTable.patient.hasHealthFundCard.metadata.id,
        comparison: ReportFilterComparison.IsEqualTo,
        values: [{ label: 'Yes', value: true }],
      }),
    ],
    [Region.Australia]
  );
}

export function healthFundExpiringCalendarYearCampaignReportTemplate(
  factTable: PatientEventFactMeasures
): ICustomReportTemplate {
  return basicPatientsCampaignReportTemplate(
    factTable,
    'Health Fund Expiring Benefits Calendar Year Campaign Report',
    'Report template for targeting active health fund patients approaching the end of calendar year benefit expiry.',
    [
      CustomReportFilter.init({
        id: factTable.patient.status.metadata.id,
        comparison: ReportFilterComparison.IsEqualTo,
        values: [{ label: 'Active', value: PatientStatus.Active }],
      }),
      CustomReportFilter.init({
        id: factTable.patient.hasHealthFundCard.metadata.id,
        comparison: ReportFilterComparison.IsEqualTo,
        values: [{ label: 'Yes', value: true }],
      }),
    ],
    [Region.Australia]
  );
}

export function healthFundExpiringFinancialYearCampaignReportTemplate(
  factTable: PatientEventFactMeasures
): ICustomReportTemplate {
  return basicPatientsCampaignReportTemplate(
    factTable,
    'Health Fund Expiring Benefits Financial Year Campaign Report',
    'Report template for targeting active health fund patients approaching the end of financial year benefit expiry.',
    [
      CustomReportFilter.init({
        id: factTable.patient.status.metadata.id,
        comparison: ReportFilterComparison.IsEqualTo,
        values: [{ label: 'Active', value: PatientStatus.Active }],
      }),
      CustomReportFilter.init({
        id: factTable.patient.hasHealthFundCard.metadata.id,
        comparison: ReportFilterComparison.IsEqualTo,
        values: [{ label: 'Yes', value: true }],
      }),
    ],
    [Region.Australia]
  );
}

export function coldContactReactivationCampaignReportTemplate(
  factTable: PatientEventFactMeasures
): ICustomReportTemplate {
  return basicPatientsCampaignReportTemplate(
    factTable,
    'Cold Contact Reactivation (2 - 5 years) Report',
    'Report template for targeting active patients who have been inactive for (2 - 5) years, have no next visit booked, are under 80 years old, and have no outstanding balance.',
    [
      CustomReportFilter.init({
        id: factTable.patient.status.metadata.id,
        comparison: ReportFilterComparison.IsEqualTo,
        values: [{ label: 'Active', value: PatientStatus.Active }],
      }),
      CustomReportFilter.init({
        id: factTable.patient.nextVisitBooked.metadata.id,
        comparison: ReportFilterComparison.IsEqualTo,
        values: [{ label: 'No', value: false }],
      }),
      CustomReportFilter.init({
        id: factTable.patient.lastVisitDate.metadata.id,
        comparison: ReportFilterComparison.DateIsAtLeast,
        dateType: DateFilterType.ReportDate,
        relativeOffset: {
          amount: 25,
          unit: TimingUnit.Months,
          direction: TimingDirection.Before,
        },
        values: [],
      }),
      CustomReportFilter.init({
        id: factTable.patient.lastVisitDate.metadata.id,
        comparison: ReportFilterComparison.DateIsAtMost,
        dateType: DateFilterType.ReportDate,
        relativeOffset: {
          amount: 60,
          unit: TimingUnit.Months,
          direction: TimingDirection.Before,
        },
        values: [],
      }),
      CustomReportFilter.init({
        id: factTable.patient.dateOfBirth.metadata.id,
        comparison: ReportFilterComparison.DateIsAtMost,
        dateType: DateFilterType.ReportDate,
        relativeOffset: {
          amount: 80,
          unit: TimingUnit.Years,
          direction: TimingDirection.Before,
        },
        values: [],
      }),
      CustomReportFilter.init({
        id: factTable.patient.outstanding.metadata.id,
        comparison: ReportFilterComparison.IsEqualTo,
        values: [{ label: '0', value: 0 }],
      }),
    ],
    ALL_REGIONS
  );
}
